import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Preview as PreviewIcon,
  RestartAlt as RestartAltIcon,
  Schedule as ScheduleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import MultilingualInput from '../components/MultilingualInput';
import MarkdownEditorMultilingual from '../components/MarkdownEditorMultilingual';
import { STUDENT_MESSAGE_SEND_VARIABLES } from '../utils/studentMessageVariables';
import {
  formatParisDateTime,
  formatScheduleDelayLabel,
  minScheduleDatetimeLocal,
  parseDatetimeLocalValue,
  MIN_SCHEDULE_DELAY_MS,
} from '../utils/studentMessageBatches';

const SECTION_SX = {
  p: 2.5,
  mb: 3,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
} as const;

type CourseOption = {
  id: string;
  courseProject?: string;
  title: string | Record<string, string>;
  defaultLanguage?: string;
  availableLanguages?: string[];
};

type TemplateOption = {
  id: string;
  key: string;
  name: string;
  description: string;
  subject: Record<string, string>;
  body: Record<string, string>;
};

type CourseStudentRow = {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  defaultLanguage?: string | null;
  lastMessageAt?: string | null;
  groupId?: string | null;
  groupName?: string | null;
};

type RecipientPreview = {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  language: string;
  groupName: string;
  lastMessageAt: string | null;
  skipped: boolean;
  skipReason?: string;
};

type MessageRowResult = {
  studentId: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  status: 'sent' | 'failed' | 'skipped';
  message: string;
};

type MessageSummary = {
  totalRecipients: number;
  sent: number;
  failed: number;
  skipped: number;
  rows: MessageRowResult[];
};

const STATUS_LABELS: Record<MessageRowResult['status'], string> = {
  sent: 'Envoyé',
  failed: 'Échec',
  skipped: 'Ignoré',
};

const STATUS_COLORS: Record<MessageRowResult['status'], 'success' | 'error' | 'default'> = {
  sent: 'success',
  failed: 'error',
  skipped: 'default',
};

const emptyMultilingual = (langs: string[]): Record<string, string> =>
  Object.fromEntries(langs.map((l) => [l, '']));

const isMultilingualComplete = (
  value: Record<string, string>,
  langs: string[]
): boolean => langs.every((lang) => Boolean(value[lang]?.trim()));

const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const StudentMessageComposer: React.FC = () => {
  const { courseId: routeCourseId } = useParams<{ courseId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const hasCourseFromRoute = Boolean(routeCourseId);
  const STEPS = hasCourseFromRoute
    ? ['Destinataires', 'Message', 'Prévisualisation', 'Rapport']
    : ['Formation', 'Destinataires', 'Message', 'Prévisualisation', 'Rapport'];

  const stepIndex = (name: string) => STEPS.indexOf(name);

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [pickedCourseId, setPickedCourseId] = useState('');
  const effectiveCourseId = routeCourseId || pickedCourseId;

  const [courseStudents, setCourseStudents] = useState<CourseStudentRow[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['fr', 'en']);
  const [subject, setSubject] = useState<Record<string, string>>({});
  const [body, setBody] = useState<Record<string, string>>({});
  const [includeAppLink, setIncludeAppLink] = useState(true);
  const [customButtonUrl, setCustomButtonUrl] = useState('');

  const [recipients, setRecipients] = useState<RecipientPreview[]>([]);
  const [courseInfo, setCourseInfo] = useState<CourseOption | null>(null);
  const [previewStudentId, setPreviewStudentId] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLanguage, setPreviewLanguage] = useState('');
  const [dryRunSummary, setDryRunSummary] = useState<MessageSummary | null>(null);
  const [finalSummary, setFinalSummary] = useState<MessageSummary | null>(null);
  const [finalBatch, setFinalBatch] = useState<{
    id: string;
    status: string;
    scheduledAt: string;
  } | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDatetime, setScheduleDatetime] = useState(minScheduleDatetimeLocal());
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const parseStudentsFromUrl = useCallback(() => {
    const raw = searchParams.get('students');
    if (!raw) return [];
    return raw.split(',').map((id) => id.trim()).filter(Boolean);
  }, [searchParams]);

  useEffect(() => {
    const fromUrl = parseStudentsFromUrl();
    if (fromUrl.length > 0) {
      setSelectedStudentIds(fromUrl);
    }
  }, [parseStudentsFromUrl]);

  useEffect(() => {
    if (hasCourseFromRoute) return;
    void (async () => {
      try {
        const res = await adminFetch(`${apiBase}/courses`);
        if (!res.ok) throw new Error('Impossible de charger les formations');
        const data = await res.json();
        setCourses(
          (data || []).map((c: CourseOption & { _id?: string }) => ({
            id: c.id || c._id || '',
            courseProject: c.courseProject,
            title: c.title,
            defaultLanguage: c.defaultLanguage,
            availableLanguages: c.availableLanguages,
          }))
        );
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement');
      }
    })();
  }, [apiBase, hasCourseFromRoute]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await adminFetch(`${apiBase}/student-messages/templates`);
        if (!res.ok) throw new Error('Impossible de charger les templates');
        const data = await res.json();
        setTemplates(data.templates || []);
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement des templates');
      }
    })();
  }, [apiBase]);

  const loadCourseStudents = useCallback(async () => {
    if (!effectiveCourseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`${apiBase}/courses/${effectiveCourseId}/students`);
      if (!res.ok) throw new Error('Impossible de charger les apprenants');
      const data = await res.json();
      const langs = data.course?.availableLanguages || ['fr', 'en'];
      setAvailableLanguages(langs);
      setSubject((prev) => ({ ...emptyMultilingual(langs), ...prev }));
      setBody((prev) => ({ ...emptyMultilingual(langs), ...prev }));
      setCourseInfo({
        id: data.course?.id || effectiveCourseId,
        courseProject: data.course?.courseProject,
        title: data.course?.title || '',
        defaultLanguage: data.course?.defaultLanguage,
        availableLanguages: langs,
      });
      setCourseStudents(
        (data.students || []).map((row: any) => ({
          studentId: row.student?.id || row.student?._id,
          firstName: row.student?.firstName || '',
          lastName: row.student?.lastName || '',
          email: row.student?.email || '',
          defaultLanguage: row.student?.defaultLanguage,
          lastMessageAt: row.student?.lastMessageAt,
          groupId: row.group?.id || row.group?._id || null,
          groupName: row.group?.name || null,
        }))
      );
      setSelectedGroupId('');
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiBase, effectiveCourseId]);

  useEffect(() => {
    if (effectiveCourseId && activeStep >= stepIndex('Destinataires')) {
      void loadCourseStudents();
    }
  }, [effectiveCourseId, activeStep, loadCourseStudents]);

  const groupFilterOptions = useMemo(() => {
    const counts = new Map<string, { id: string; name: string; count: number }>();
    courseStudents.forEach((student) => {
      const id = student.groupId || '__none__';
      const name = student.groupName || 'Sans groupe';
      const existing = counts.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(id, { id, name, count: 1 });
      }
    });
    return Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [courseStudents]);

  const filteredCourseStudents = useMemo(() => {
    if (!selectedGroupId) return courseStudents;
    if (selectedGroupId === '__none__') {
      return courseStudents.filter((student) => !student.groupId);
    }
    return courseStudents.filter((student) => student.groupId === selectedGroupId);
  }, [courseStudents, selectedGroupId]);

  const toggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredCourseStudents.map((s) => s.studentId);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedStudentIds.includes(id));
    if (allSelected) {
      setSelectedStudentIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedStudentIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    const nextSubject = emptyMultilingual(availableLanguages);
    const nextBody = emptyMultilingual(availableLanguages);
    availableLanguages.forEach((lang) => {
      nextSubject[lang] = template.subject[lang] || '';
      nextBody[lang] = template.body[lang] || '';
    });
    setSubject(nextSubject);
    setBody(nextBody);
  };

  const buildContentPayload = () => ({
    studentIds: selectedStudentIds,
    subject,
    body,
    templateId: selectedTemplate?.id,
    templateKey: selectedTemplate?.key,
    includeMagicLink: includeAppLink,
    customButtonUrl: includeAppLink ? undefined : customButtonUrl.trim(),
  });

  const canProceedFromDestinataires = selectedStudentIds.length > 0;

  const canProceedFromMessage =
    isMultilingualComplete(subject, availableLanguages) &&
    isMultilingualComplete(body, availableLanguages) &&
    (includeAppLink || isValidHttpUrl(customButtonUrl));

  const handlePreview = async () => {
    if (!effectiveCourseId) return;
    setLoading(true);
    setError('');
    try {
      const [recipientsRes, dryRunRes] = await Promise.all([
        adminFetch(`${apiBase}/student-messages/courses/${effectiveCourseId}/preview-recipients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ studentIds: selectedStudentIds }),
        }),
        adminFetch(`${apiBase}/student-messages/courses/${effectiveCourseId}/preview-send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildContentPayload()),
        }),
      ]);

      const recipientsData = await recipientsRes.json();
      const dryRunData = await dryRunRes.json();

      if (!recipientsRes.ok) {
        throw new Error(recipientsData?.error || recipientsData?.message || 'Erreur prévisualisation destinataires');
      }
      if (!dryRunRes.ok) {
        throw new Error(dryRunData?.error || dryRunData?.message || 'Erreur simulation d\'envoi');
      }

      const recipientList: RecipientPreview[] = recipientsData.recipients || [];
      setRecipients(recipientList);
      setDryRunSummary(dryRunData.summary as MessageSummary);
      if (recipientsData.course?.availableLanguages) {
        setAvailableLanguages(recipientsData.course.availableLanguages);
      }

      const firstEligible = recipientList.find((r) => !r.skipped);
      if (firstEligible) {
        setPreviewStudentId(firstEligible.studentId);
        await loadHtmlPreview(firstEligible.studentId);
      } else {
        setPreviewStudentId('');
        setPreviewHtml('');
        setPreviewSubject('');
      }

      setActiveStep(stepIndex('Prévisualisation'));
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };

  const loadHtmlPreview = async (studentId: string) => {
    if (!effectiveCourseId || !studentId) return;
    try {
      const res = await adminFetch(
        `${apiBase}/student-messages/courses/${effectiveCourseId}/preview-render`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...buildContentPayload(),
            previewStudentId: studentId,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Erreur rendu HTML');
      }
      setPreviewSubject(data.subject || '');
      setPreviewHtml(data.html || '');
      setPreviewLanguage(data.language || '');
    } catch (err: any) {
      setError(err.message || 'Erreur rendu HTML');
    }
  };

  const handleSend = async () => {
    if (!effectiveCourseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(
        `${apiBase}/student-messages/courses/${effectiveCourseId}/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildContentPayload()),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Erreur lors de l\'envoi');
      }
      setFinalBatch(data.batch || null);
      setFinalSummary(data.summary || data.batch?.summary || null);
      setActiveStep(stepIndex('Rapport'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleConfirm = async () => {
    if (!effectiveCourseId) return;
    const scheduledAt = parseDatetimeLocalValue(scheduleDatetime);
    if (scheduledAt.getTime() < Date.now() + MIN_SCHEDULE_DELAY_MS) {
      setError('La planification doit être au minimum 15 minutes dans le futur');
      return;
    }

    setLoading(true);
    setError('');
    setScheduleConfirmOpen(false);
    setScheduleDialogOpen(false);
    try {
      const res = await adminFetch(
        `${apiBase}/student-messages/courses/${effectiveCourseId}/batches`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...buildContentPayload(),
            scheduledAt: scheduledAt.toISOString(),
            sendNow: false,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Erreur lors de la planification');
      }
      setFinalBatch(data.batch);
      setFinalSummary(null);
      setActiveStep(stepIndex('Rapport'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la planification');
    } finally {
      setLoading(false);
    }
  };

  const openScheduleConfirm = () => {
    const scheduledAt = parseDatetimeLocalValue(scheduleDatetime);
    if (scheduledAt.getTime() < Date.now() + MIN_SCHEDULE_DELAY_MS) {
      setError('La planification doit être au minimum 15 minutes dans le futur');
      return;
    }
    setScheduleConfirmOpen(true);
  };

  const openSendConfirm = () => {
    if (!dryRunSummary || dryRunSummary.sent === 0) return;
    setSendConfirmOpen(true);
  };

  const handleSendConfirm = async () => {
    setSendConfirmOpen(false);
    await handleSend();
  };

  const handleReset = () => {
    setActiveStep(0);
    setError('');
    setPickedCourseId('');
    setSelectedStudentIds(parseStudentsFromUrl());
    setSelectedTemplateId('');
    setSubject(emptyMultilingual(availableLanguages));
    setBody(emptyMultilingual(availableLanguages));
    setIncludeAppLink(true);
    setCustomButtonUrl('');
    setRecipients([]);
    setDryRunSummary(null);
    setFinalSummary(null);
    setFinalBatch(null);
    setScheduleDialogOpen(false);
    setScheduleConfirmOpen(false);
    setSendConfirmOpen(false);
    setScheduleDatetime(minScheduleDatetimeLocal());
    setPreviewHtml('');
    setPreviewSubject('');
  };

  const renderSummaryChips = (summary: MessageSummary) => (
    <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
      <Chip label={`Total : ${summary.totalRecipients}`} />
      <Chip color="success" label={`Envoyés : ${summary.sent}`} />
      {summary.failed > 0 && <Chip color="error" label={`Échecs : ${summary.failed}`} />}
      <Chip label={`Ignorés : ${summary.skipped}`} />
    </Box>
  );

  const renderReportTable = (summary: MessageSummary) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Prénom</TableCell>
            <TableCell>Nom</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Langue</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Détail</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.rows.map((row) => (
            <TableRow key={`${row.studentId}-${row.email}`}>
              <TableCell>{row.firstName}</TableCell>
              <TableCell>{row.lastName}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.language}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={STATUS_COLORS[row.status]}
                  label={STATUS_LABELS[row.status]}
                />
              </TableCell>
              <TableCell>{row.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const selectedCourseLabel = courseInfo
    ? getAdminCourseName(courseInfo)
    : courses.find((c) => c.id === pickedCourseId)
      ? getAdminCourseName(courses.find((c) => c.id === pickedCourseId)!)
      : '';

  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          navigate(
            hasCourseFromRoute && routeCourseId
              ? `/courses/${routeCourseId}/students`
              : '/student-messages'
          )
        }
        sx={{ mb: 2 }}
      >
        Retour
      </Button>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Message apprenant
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Composez et envoyez un email personnalisé aux apprenants d&apos;une formation.
          {selectedCourseLabel ? ` — ${selectedCourseLabel}` : ''}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {STEPS[activeStep] === 'Formation' && (
        <Card>
          <CardContent>
            <Paper variant="outlined" sx={SECTION_SX}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Choisir une formation
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Formation</InputLabel>
                <Select
                  value={pickedCourseId}
                  label="Formation"
                  onChange={(e) => setPickedCourseId(e.target.value)}
                >
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {getAdminCourseName(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disabled={!pickedCourseId}
                onClick={() => setActiveStep(stepIndex('Destinataires'))}
              >
                Continuer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {STEPS[activeStep] === 'Destinataires' && (
        <Card>
          <CardContent>
            <Paper variant="outlined" sx={SECTION_SX}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Sélection des destinataires
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {selectedStudentIds.length} apprenant{selectedStudentIds.length !== 1 ? 's' : ''}{' '}
                sélectionné{selectedStudentIds.length !== 1 ? 's' : ''}
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {groupFilterOptions.length > 1 && (
                    <Box mb={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        Filtrer par groupe :
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={`Tous (${courseStudents.length})`}
                          color={selectedGroupId === '' ? 'primary' : 'default'}
                          variant={selectedGroupId === '' ? 'filled' : 'outlined'}
                          onClick={() => setSelectedGroupId('')}
                        />
                        {groupFilterOptions.map((group) => (
                          <Chip
                            key={group.id}
                            label={`${group.name} (${group.count})`}
                            color={selectedGroupId === group.id ? 'primary' : 'default'}
                            variant={selectedGroupId === group.id ? 'filled' : 'outlined'}
                            onClick={() => setSelectedGroupId(group.id)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                filteredCourseStudents.length > 0 &&
                                filteredCourseStudents.every((s) =>
                                  selectedStudentIds.includes(s.studentId)
                                )
                              }
                              indeterminate={
                                filteredCourseStudents.some((s) =>
                                  selectedStudentIds.includes(s.studentId)
                                ) &&
                                !filteredCourseStudents.every((s) =>
                                  selectedStudentIds.includes(s.studentId)
                                )
                              }
                              onChange={toggleSelectAll}
                            />
                          </TableCell>
                          <TableCell>Prénom</TableCell>
                          <TableCell>Nom</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Groupe</TableCell>
                          <TableCell>Langue</TableCell>
                          <TableCell>Dernier message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCourseStudents.map((student) => (
                          <TableRow key={student.studentId} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedStudentIds.includes(student.studentId)}
                                onChange={() => toggleStudent(student.studentId)}
                              />
                            </TableCell>
                            <TableCell>{student.firstName}</TableCell>
                            <TableCell>{student.lastName}</TableCell>
                            <TableCell>{student.email}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={student.groupName || 'Sans groupe'}
                              />
                            </TableCell>
                            <TableCell>{student.defaultLanguage || '—'}</TableCell>
                            <TableCell>
                              {student.lastMessageAt
                                ? new Date(student.lastMessageAt).toLocaleDateString('fr-FR')
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
            <Box display="flex" justifyContent="space-between">
              {!hasCourseFromRoute && (
                <Button onClick={() => setActiveStep(stepIndex('Formation'))}>Retour</Button>
              )}
              <Box flex={1} />
              <Button
                variant="contained"
                disabled={!canProceedFromDestinataires}
                onClick={() => setActiveStep(stepIndex('Message'))}
              >
                Continuer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {STEPS[activeStep] === 'Message' && (
        <Card>
          <CardContent>
            <Paper variant="outlined" sx={SECTION_SX}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Contenu du message
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  <MenuItem value="">Aucun (saisie manuelle)</MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedTemplate?.description && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {selectedTemplate.description}
                </Typography>
              )}
              <MultilingualInput
                label="Objet"
                value={subject}
                onChange={setSubject}
                availableLanguages={availableLanguages}
                required
              />
              <MarkdownEditorMultilingual
                label="Corps du message"
                value={body}
                onChange={setBody}
                availableLanguages={availableLanguages}
              />
            </Paper>

            <Paper variant="outlined" sx={SECTION_SX}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Lien du bouton
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAppLink}
                    onChange={(e) => setIncludeAppLink(e.target.checked)}
                  />
                }
                label="Lien vers l'application Fasterclass (magic link)"
              />
              {!includeAppLink && (
                <TextField
                  label="URL du bouton"
                  fullWidth
                  value={customButtonUrl}
                  onChange={(e) => setCustomButtonUrl(e.target.value)}
                  placeholder="https://..."
                  helperText="Cette URL remplace le magic link. Aucun lien de connexion Fasterclass ne sera généré."
                  error={customButtonUrl.trim().length > 0 && !isValidHttpUrl(customButtonUrl)}
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>

            <Paper variant="outlined" sx={SECTION_SX}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                Variables disponibles à l&apos;envoi
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Les variables atelier sont résolues automatiquement selon le groupe de chaque
                apprenant.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {STUDENT_MESSAGE_SEND_VARIABLES.map((v) => (
                  <Chip key={v.key} label={`${v.label} — ${v.key}`} size="small" variant="outlined" />
                ))}
              </Box>
            </Paper>
            <Box display="flex" justifyContent="space-between">
              <Button onClick={() => setActiveStep(stepIndex('Destinataires'))}>Retour</Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />}
                disabled={!canProceedFromMessage || loading}
                onClick={() => void handlePreview()}
              >
                Prévisualiser
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {STEPS[activeStep] === 'Prévisualisation' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Prévisualisation
            </Typography>
            {dryRunSummary && renderSummaryChips(dryRunSummary)}
            <Paper variant="outlined" sx={{ ...SECTION_SX, mb: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Nom</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Groupe</TableCell>
                      <TableCell>Langue</TableCell>
                      <TableCell>Dernier message</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipients.map((r) => (
                      <TableRow
                        key={r.studentId}
                        hover
                        selected={previewStudentId === r.studentId}
                        onClick={() => {
                          if (!r.skipped) {
                            setPreviewStudentId(r.studentId);
                            void loadHtmlPreview(r.studentId);
                          }
                        }}
                        sx={{ cursor: r.skipped ? 'default' : 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={previewStudentId === r.studentId}
                            disabled={r.skipped}
                            onChange={() => {
                              setPreviewStudentId(r.studentId);
                              void loadHtmlPreview(r.studentId);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {r.firstName} {r.lastName}
                        </TableCell>
                        <TableCell>{r.email}</TableCell>
                        <TableCell>{r.groupName || '—'}</TableCell>
                        <TableCell>{r.language}</TableCell>
                        <TableCell>
                          {r.lastMessageAt
                            ? new Date(r.lastMessageAt).toLocaleDateString('fr-FR')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {r.skipped ? (
                            <Chip size="small" label={r.skipReason || 'Ignoré'} />
                          ) : (
                            <Chip size="small" color="success" label="Éligible" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {previewSubject && (
              <Paper variant="outlined" sx={{ ...SECTION_SX, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Objet ({previewLanguage})
                </Typography>
                <Typography variant="body1">{previewSubject}</Typography>
              </Paper>
            )}

            {previewHtml && (
              <Paper variant="outlined" sx={SECTION_SX}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Aperçu HTML
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#f5f5f5',
                  }}
                >
                  <iframe
                    title="Aperçu du message"
                    srcDoc={previewHtml}
                    style={{ width: '100%', height: 480, border: 'none' }}
                    sandbox=""
                  />
                </Box>
              </Paper>
            )}

            <Box display="flex" justifyContent="space-between" mt={3} flexWrap="wrap" gap={1}>
              <Button onClick={() => setActiveStep(stepIndex('Message'))} disabled={loading}>
                Retour
              </Button>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  disabled={loading || !dryRunSummary || dryRunSummary.sent === 0}
                  onClick={() => {
                    setScheduleDatetime(minScheduleDatetimeLocal());
                    setScheduleDialogOpen(true);
                  }}
                >
                  Planifier l&apos;envoi
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  disabled={loading || !dryRunSummary || dryRunSummary.sent === 0}
                  onClick={openSendConfirm}
                >
                  Envoyer maintenant
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {STEPS[activeStep] === 'Rapport' && (finalSummary || finalBatch) && (
        <Card>
          <CardContent>
            {finalBatch?.status === 'scheduled' ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Envoi planifié
                </Typography>
                <Typography variant="body2" mb={1}>
                  Le message sera envoyé le {formatParisDateTime(finalBatch.scheduledAt)}.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/student-messages/batches/${finalBatch.id}`)}
                >
                  Voir le lot planifié
                </Button>
              </Alert>
            ) : (
              <Alert
                severity="success"
                icon={<CheckCircleIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Envoi terminé
                </Typography>
                {finalSummary && (
                  <Typography variant="body2">
                    {finalSummary.sent} message{finalSummary.sent !== 1 ? 's' : ''} envoyé
                    {finalSummary.sent !== 1 ? 's' : ''}
                    {finalSummary.failed > 0
                      ? `, ${finalSummary.failed} échec${finalSummary.failed > 1 ? 's' : ''}`
                      : ''}
                    {finalSummary.skipped > 0
                      ? `, ${finalSummary.skipped} ignoré${finalSummary.skipped > 1 ? 's' : ''}`
                      : ''}
                    .
                  </Typography>
                )}
              </Alert>
            )}
            {finalSummary && (
              <>
                <Typography variant="h6" gutterBottom>
                  Rapport d&apos;envoi
                </Typography>
                {renderSummaryChips(finalSummary)}
                {renderReportTable(finalSummary)}
              </>
            )}
            <Box mt={3} display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
                Nouvel envoi
              </Button>
              {finalBatch && (
                <Button variant="text" onClick={() => navigate('/student-messages/batches')}>
                  Voir tous les envois
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Planifier l&apos;envoi</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choisissez la date et l&apos;heure d&apos;envoi. L&apos;envoi doit être planifié au
            minimum 15 minutes à l&apos;avance.
          </DialogContentText>
          <TextField
            label="Date et heure d'envoi"
            type="datetime-local"
            fullWidth
            value={scheduleDatetime}
            onChange={(e) => setScheduleDatetime(e.target.value)}
            inputProps={{ min: minScheduleDatetimeLocal() }}
            helperText="Heure locale du navigateur"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={openScheduleConfirm}>
            Continuer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scheduleConfirmOpen} onClose={() => setScheduleConfirmOpen(false)}>
        <DialogTitle>Confirmer la planification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ce message sera envoyé{' '}
            {formatScheduleDelayLabel(parseDatetimeLocalValue(scheduleDatetime))}, le{' '}
            {formatParisDateTime(parseDatetimeLocalValue(scheduleDatetime).toISOString())}.
            <br />
            <br />
            Souhaitez-vous confirmer la planification ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleConfirmOpen(false)}>Retour</Button>
          <Button variant="contained" onClick={() => void handleScheduleConfirm()} disabled={loading}>
            Confirmer la planification
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendConfirmOpen} onClose={() => setSendConfirmOpen(false)}>
        <DialogTitle>Confirmer l&apos;envoi immédiat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dryRunSummary && dryRunSummary.sent > 0 ? (
              <>
                <strong>
                  {dryRunSummary.sent} e-mail{dryRunSummary.sent > 1 ? 's' : ''}
                </strong>{' '}
                {dryRunSummary.sent > 1 ? 'seront envoyés' : 'sera envoyé'} immédiatement.
                {dryRunSummary.skipped > 0 && (
                  <>
                    <br />
                    <br />
                    {dryRunSummary.skipped} destinataire{dryRunSummary.skipped > 1 ? 's' : ''}{' '}
                    {dryRunSummary.skipped > 1 ? 'seront ignorés' : 'sera ignoré'} (SCORM sans
                    e-mail, etc.).
                  </>
                )}
                <br />
                <br />
                Souhaitez-vous confirmer l&apos;envoi ?
              </>
            ) : (
              'Aucun e-mail ne peut être envoyé pour le moment.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={() => void handleSendConfirm()}
            disabled={loading || !dryRunSummary || dryRunSummary.sent === 0}
          >
            Confirmer l&apos;envoi
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudentMessageComposer;
